import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { Box, Typography, Button, Paper, Skeleton, Alert, Avatar, Card, CardContent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SimpleTransactionForm from '../components/SimpleTransactionForm';
import LazisnuTransactionTabs from '../components/LazisnuTransactionTabs';
import LazisnuTransactionTable from '../components/LazisnuTransactionTable';
import useLazisnuTransactionStore from '../store/useLazisnuTransactionStore';
import useNotificationStore from '../store/useNotificationStore';
import useAuthStore from '../store/useAuthStore';

const LazisnuTransactionPage = () => {
  const { user } = useAuthStore();
  const isLapangan = user?.jabatan === 'LAPANGAN';

  const {
    transactions,
    loading,
    error,
    fetchTransactions,
    addPenerimaanInfak,
    addPenerimaanZakat,
    addPenerimaanDanaAmil,
    addPenyaluran,
    addBebanPengelolaan,
    updateTransaction,
    removeTransaction,
  } = useLazisnuTransactionStore();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleAddOrUpdate = async (transactionData) => {
    try {
      if (editingTransaction) {
        await updateTransaction(editingTransaction.id, transactionData);
        showNotification('Transaksi berhasil diperbarui', 'success');
      } else {
        switch (activeTab) {
          case 0: await addPenerimaanInfak(transactionData); break;
          case 1: await addPenerimaanDanaAmil(transactionData); break;
          case 2: await addPenerimaanZakat(transactionData); break;
          case 3: await addPenyaluran(transactionData); break;
          case 4: await addBebanPengelolaan(transactionData); break;
          default: throw new Error('Invalid transaction type');
        }
        showNotification('Transaksi berhasil ditambahkan', 'success');
      }
      setShowForm(false);
      setEditingTransaction(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      showNotification(`Gagal menyimpan transaksi: ${errorMessage}`, 'error');
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus transaksi ini?')) {
      try {
        await removeTransaction(id);
        showNotification('Transaksi berhasil dihapus', 'success');
      } catch (err) {
        showNotification(`Gagal menghapus transaksi: ${err}`, 'error');
      }
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingTransaction(null);
  };

  if (error) return <MainLayout><Box sx={{ p: 3 }}><Alert severity="error">Error: {error}</Alert></Box></MainLayout>;

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        {/* HEADER */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: {xs: 2, md: 0} }}>
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main', mr: 2, width: 56, height: 56 }}>
                    <ReceiptIcon fontSize="large" />
                </Avatar>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary.main">Transaksi Lazisnu</Typography>
                    <Typography variant="body2" color="text.secondary">Kelola pencatatan transaksi keuangan pusat.</Typography>
                </Box>
            </Box>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setEditingTransaction(null); setShowForm(true); }}
                sx={{ borderRadius: 2, px: 3 }}
            >
                Tambah Transaksi
            </Button>
        </Paper>

        {showForm && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 3 }}>
            {editingTransaction ? (
              <SimpleTransactionForm
                title="Edit Transaksi"
                initialData={editingTransaction}
                onSubmit={handleAddOrUpdate}
                onCancel={handleCancelForm}
                type={editingTransaction.jenis === 'PEMASUKAN' ? 'penerimaan' : 'penyaluran'}
              />
            ) : (
              <LazisnuTransactionTabs
                onSubmit={handleAddOrUpdate}
                onCancel={handleCancelForm}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isLapangan={isLapangan}
              />
            )}
          </Paper>
        )}

        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Riwayat Transaksi</Typography>
                {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={300} />
                ) : (
                    <LazisnuTransactionTable
                        transactions={transactions}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                )}
            </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default LazisnuTransactionPage;
